import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import {
  getConsent,
  setConsent,
  acceptAll,
  rejectAll,
  hasDecided,
} from "@/lib/consent";
import { track, Events } from "@/lib/analytics";

/**
 * Cookie consent banner + preferences modal.
 *
 * - On first visit, slides up from bottom asking the visitor to accept,
 *   reject non-essential, or open preferences.
 * - After a decision, the banner stays dismissed; the Footer "Cookie
 *   preferences" link dispatches `sideb:open-cookie-preferences` to reopen
 *   the modal here so the visitor can change their mind.
 *
 * The actual gating of PostHog / Brevo lives in `App.js` and `lib/chat.js`
 * — this component only writes consent state.
 */
export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [draftAnalytics, setDraftAnalytics] = useState(false);
  const [draftChat, setDraftChat] = useState(false);

  // Initial banner check + listen for the Footer's "reopen preferences" event.
  useEffect(() => {
    setShowBanner(!hasDecided());
    const reopen = () => {
      const current = getConsent();
      setDraftAnalytics(current.analytics);
      setDraftChat(current.chat);
      setShowModal(true);
    };
    window.addEventListener("sideb:open-cookie-preferences", reopen);
    return () => window.removeEventListener("sideb:open-cookie-preferences", reopen);
  }, []);

  const handleAcceptAll = () => {
    acceptAll();
    // Fire AFTER setConsent so App.js has already called initAnalytics(). This
    // first event marks the start of the "consented" funnel in PostHog.
    track(Events.ConsentChanged, { action: "accept_all", analytics: true, chat: true });
    setShowBanner(false);
    setShowModal(false);
  };

  const handleRejectAll = () => {
    rejectAll();
    // Analytics is OFF so this track() is a no-op — that's the point. Rejecters
    // are invisible to PostHog by design; the rejection rate is inferred from
    // (visit count) − (consent_changed accept events) at the server level.
    track(Events.ConsentChanged, { action: "reject_all", analytics: false, chat: false });
    setShowBanner(false);
    setShowModal(false);
  };

  const handleOpenManage = () => {
    const current = getConsent();
    setDraftAnalytics(current.analytics);
    setDraftChat(current.chat);
    setShowModal(true);
  };

  const handleSaveModal = () => {
    setConsent({ analytics: draftAnalytics, chat: draftChat });
    track(Events.ConsentChanged, {
      action: "save_preferences",
      analytics: draftAnalytics,
      chat: draftChat,
    });
    setShowBanner(false);
    setShowModal(false);
  };

  if (!showBanner && !showModal) return null;

  return (
    <>
      {showBanner && !showModal && (
        <div
          className="fixed bottom-4 left-4 right-4 lg:left-auto lg:right-6 lg:bottom-6 lg:max-w-md z-[60] rounded-2xl bg-ink text-white shadow-2xl border border-white/10 p-5 animate-in slide-in-from-bottom-4 fade-in duration-300"
          role="dialog"
          aria-label="Cookie consent"
          data-testid="cookie-banner"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="label-mono" style={{ color: "rgba(255,255,255,0.55)" }}>Cookies</div>
            <button
              type="button"
              onClick={handleRejectAll}
              className="text-white/40 hover:text-white/80 transition-colors -mr-1 -mt-1"
              aria-label="Reject non-essential cookies and close"
              data-testid="cookie-banner-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <h3 className="font-display text-lg mt-2 leading-snug font-semibold tracking-tight">
            We use a small set of cookies.
          </h3>
          <p className="text-sm text-white/70 mt-2 leading-relaxed">
            Authentication is essential. Analytics (PostHog) and live chat (Brevo) are optional — enable them only if you&apos;re comfortable.{" "}
            <Link to="/privacy#cookies" className="underline hover:text-[#ac62ea]" data-testid="cookie-banner-details-link">
              Details
            </Link>
            .
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleAcceptAll}
              className="rounded-full bg-white text-[#0b1e5a] px-4 py-2 text-sm font-semibold hover:bg-[#ac62ea] hover:text-white transition-colors"
              data-testid="cookie-accept-all"
            >
              Accept all
            </button>
            <button
              type="button"
              onClick={handleRejectAll}
              className="rounded-full bg-white/10 text-white px-4 py-2 text-sm font-semibold hover:bg-white/20 transition-colors"
              data-testid="cookie-reject-all"
            >
              Reject non-essential
            </button>
            <button
              type="button"
              onClick={handleOpenManage}
              className="rounded-full text-white/80 px-3 py-2 text-sm font-medium hover:text-white underline-offset-4 hover:underline"
              data-testid="cookie-manage"
            >
              Manage
            </button>
          </div>
        </div>
      )}

      {showModal && (
        <div
          className="fixed inset-0 z-[70] flex items-end lg:items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200"
          role="dialog"
          aria-modal="true"
          aria-label="Cookie preferences"
          data-testid="cookie-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 lg:p-8 animate-in slide-in-from-bottom-4 lg:slide-in-from-bottom-0 duration-300">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="label-mono">Cookies</div>
                <h3 className="font-display text-2xl mt-2 font-semibold tracking-tight">Cookie preferences</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-muted-foreground hover:text-foreground transition-colors -mr-1 -mt-1"
                aria-label="Close preferences"
                data-testid="cookie-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              Choose which categories to enable. You can change this any time from the &quot;Cookie preferences&quot; link in the footer.
            </p>

            <div className="mt-6 space-y-3">
              <ToggleRow
                title="Strictly necessary"
                subtitle="Authentication session (HttpOnly cookie). Required to keep you signed in."
                checked
                disabled
                testid="cookie-toggle-necessary"
              />
              <ToggleRow
                title="Product analytics"
                subtitle="PostHog — anonymous pageviews and funnel measurement. No advertising profile."
                checked={draftAnalytics}
                onChange={setDraftAnalytics}
                testid="cookie-toggle-analytics"
              />
              <ToggleRow
                title="Live chat"
                subtitle="Brevo Conversations — visitor ID so we can resume your chat with our team."
                checked={draftChat}
                onChange={setDraftChat}
                testid="cookie-toggle-chat"
              />
            </div>

            <div className="mt-6 flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                onClick={handleRejectAll}
                className="rounded-full bg-muted text-foreground px-4 py-2 text-sm font-medium hover:bg-muted/70 transition-colors"
                data-testid="cookie-modal-reject"
              >
                Reject non-essential
              </button>
              <button
                type="button"
                onClick={handleSaveModal}
                className="rounded-full bg-[#0b1e5a] hover:bg-black text-white px-5 py-2 text-sm font-semibold transition-colors"
                data-testid="cookie-modal-save"
              >
                Save preferences
              </button>
              <button
                type="button"
                onClick={handleAcceptAll}
                className="rounded-full bg-[#ac62ea] hover:bg-[#7d3fbf] text-white px-5 py-2 text-sm font-semibold transition-colors"
                data-testid="cookie-modal-accept-all"
              >
                Accept all
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              See the full list in our{" "}
              <Link to="/privacy#cookies" className="underline hover:text-[#ac62ea]" onClick={() => setShowModal(false)}>
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </div>
      )}
    </>
  );
}

function ToggleRow({ title, subtitle, checked, onChange, disabled, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 p-4 rounded-xl border border-border bg-ice-soft/40">
      <div className="min-w-0">
        <div className="font-display font-semibold text-sm">{title}</div>
        <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{subtitle}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange?.(!checked)}
        className={[
          "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
          checked ? "bg-[#ac62ea]" : "bg-muted-foreground/30",
          disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
        ].join(" ")}
        data-testid={testid}
      >
        <span
          className={[
            "inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform",
            checked ? "translate-x-5" : "translate-x-0.5",
          ].join(" ")}
        />
      </button>
    </div>
  );
}
